import type React from "react"
import { useState, useEffect } from "react"
import { Layout, Modal, Form, Input, Select, Card } from "antd"
import { PlusOutlined, CalculatorOutlined } from "@ant-design/icons"
import styled from "@emotion/styled"
import { ModernButton } from "components/buttons/modernButton"
import { SecondaryButton } from "components/buttons/secondaryButton"
import { Title } from "components/typography"
import { RuleList } from "components/feedback/lists/ruleList"
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useProductRuleOpenApi from "apiClients/useProductRuleOpenApiClient";
import { getRankingEffectDisplayName, getRuleTypeDisplayName, ProductRuleDto, RankingEffectEnum, RuleTypeEnum } from "interfaces/apiTypes"
import FullScreenLoader from "components/common/loadIndicators/fullScreenLoader";
import { ProductRankingList } from "components/feedback/lists/productRankingList"
import { useDebounce } from "components/hooks/useDebounce"

const { Content } = Layout
const { Option } = Select

interface Product {
  id: number
  name: string
  description: string
  tags: string[]
  timestamp: string
}

const StyledCard = styled(Card)`
  .ant-page-header-heading-left {
    display: none;
  }

  .ant-page-header-heading {
    display: none;
  }

  .ant-card-body {
    padding-bottom: 0;
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: row;
  background: #f5f5f5;
  margin: 0 -24px -24px;
  padding: 16px 24px 24px;
  min-height: calc(100vh - 280px);
  gap: 16px;
`;

const RankingPanel = styled.div`
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
`;

export const FeedbackList: React.FC = () => {
  const [rules, setRules] = useState<ProductRuleDto[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [rankingPage, setRankingPage] = useState<number>(1);
  const [rankingPageSize, setRankingPageSize] = useState<number>(50);
  const [selectedRuleType, setSelectedRuleType] = useState<RuleTypeEnum | undefined>(undefined);
  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchInput, 200);

  useEffect(() => {
    setRankingPage(1);
  }, [debouncedSearchTerm]); 

  const [products, setProducts] = useState<Product[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingRule, setEditingRule] = useState<ProductRuleDto | null>(null)
  const [form] = Form.useForm()

  const {
    getProductRules,
    createProductRule,
    updateProductRule,
    deleteProductRule,
    recalculateProductRules,
    getProductRankings,
    getPinnedProductRankings,
    pinProductScore,
    unpinProductScore,
    reorderPinnedProductScores,
  } = useProductRuleOpenApi();

  const queryClient = useQueryClient();

  const {
    data: apiRulesPaginationResult,
    isLoading,
    isError
} = useQuery(['productRules', 'page', currentPage, 'pageSize', currentPageSize], () => getProductRules({ page: currentPage, pageSize: currentPageSize }));

  const {
    data: apiPinnedRankings,
    isFetching: isFetchingPinnedRankings,
  } = useQuery(['productRankings', 'pinned'], () => getPinnedProductRankings());

  const {
    data: apiProductRankings,
    isLoading: isLoadingProductRankings,
    isFetching: isFetchingProductRankings,
    isError: isErrorProductRankings
  } = useQuery(
    ['productRankings', 'unpinned', 'page', rankingPage, 'pageSize', rankingPageSize, 'search', debouncedSearchTerm],
    () => getProductRankings({
      page: rankingPage,
      pageSize: rankingPageSize,
      search: debouncedSearchTerm,
      pinned: false,
    }),
  );

  const invalidateRankingQueries = () => {
    queryClient.invalidateQueries(['productRules', 'page', currentPage, 'pageSize', currentPageSize]);
    queryClient.invalidateQueries(['productRankings']);
  };

  const pinMutation = useMutation({
    mutationFn: (productScoreId: number) => pinProductScore(productScoreId),
    onSuccess: invalidateRankingQueries,
  });

  const unpinMutation = useMutation({
    mutationFn: (productScoreId: number) => unpinProductScore(productScoreId),
    onSuccess: invalidateRankingQueries,
  });

  const reorderPinnedMutation = useMutation({
    mutationFn: (productScoreIds: number[]) => reorderPinnedProductScores(productScoreIds),
    onSuccess: invalidateRankingQueries,
  });

  const createMutation = useMutation({
    mutationFn: createProductRule,
    onSuccess: invalidateRankingQueries,
  });

  const recalculateMutation = useMutation({
    mutationFn: recalculateProductRules,
    onSuccess: invalidateRankingQueries,
  });

  const updateMutation = useMutation({
    mutationFn: (rule: ProductRuleDto) => updateProductRule(rule.id ?? 0, rule),
    onSuccess: invalidateRankingQueries,
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: number) => deleteProductRule(ruleId),
    onSuccess: invalidateRankingQueries,
  });

  useEffect(() => {
    if (apiRulesPaginationResult) {
      setRules(apiRulesPaginationResult.data ?? []);
    }
  }, [apiRulesPaginationResult]);

  const handleDelete = (ruleId: number) => {
    deleteMutation.mutate(ruleId);
  }

  const showModal = (rule?: ProductRuleDto) => {
    if (rule) {
      setEditingRule(rule)
      form.setFieldsValue({
        ...rule,
        ruleType: rule.ruleType,
        startDate: rule.startDate ? new Date(rule.startDate).toISOString().split('T')[0] : undefined,
        endDate: rule.endDate ? new Date(rule.endDate).toISOString().split('T')[0] : undefined
      })
    } else {
      setEditingRule(null)
      form.resetFields()
      // Set default dates for new rules
      const today = new Date()
      const nextYear = new Date()
      nextYear.setFullYear(today.getFullYear() + 1)
      
      form.setFieldsValue({
        startDate: new Date(today.getTime() - (2 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
        endDate: nextYear.toISOString().split('T')[0],
        ruleType: RuleTypeEnum.Brand,
        effect: RankingEffectEnum.Boost,
      })
    }
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields()
      .then((values) => {
        const newRule: ProductRuleDto = {
          id: editingRule?.id || Date.now(),
          ...values,
          ruleType: Number(values.ruleType),
          effect: Number(values.effect),
          startDate: values.startDate ? new Date(values.startDate).toISOString() : null,
          endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
          excludeExpr: values.excludeExpr || null,
        }

        if (editingRule) {
          updateMutation.mutate(newRule);
        } else {
          createMutation.mutate(newRule);
        }

        setIsModalVisible(false);
        form.resetFields();
      })
      .catch((error) => {
        console.error('Validation failed:', error);
      });
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  }

  const handleRecalculateRules = () => {
    Modal.confirm({
      title: 'Är du säker?',
      content: 'Vill du verkligen räkna om alla regler?',
      okText: 'Ja',
      cancelText: 'Avbryt',
      onOk: () => {
        recalculateMutation.mutate();
      },
    });
  };

  // Get isLoading states from all mutations and queries
  const isLoadingInitial = isLoading;
  const isLoadingCreate = createMutation.isLoading;
  const isLoadingUpdate = updateMutation.isLoading;
  const isLoadingDelete = deleteMutation.isLoading;
  const isLoadingRecalculate = recalculateMutation.isLoading;

  // Combine all loading states
  const isLoadingAny = isLoadingInitial || 
                     isLoadingCreate || 
                     isLoadingUpdate || 
                     isLoadingDelete || 
                     isLoadingRecalculate;

  // Get appropriate loading message based on which operation is in progress
  const getLoadingMessage = () => {
    if (isLoadingCreate) return "Skapar regel...";
    if (isLoadingUpdate) return "Uppdaterar regel...";
    if (isLoadingDelete) return "Tar bort regel...";
    if (isLoadingRecalculate) return "Räknar om alla regler...";
    return "Laddar produktregler...";
  };

  const getExprPlaceholder = (ruleType?: RuleTypeEnum) => {
    switch (ruleType) {
      case RuleTypeEnum.Brand:
        return "pela,ryobi";
      case RuleTypeEnum.Category:
        return "1023,1024,1025";
      case RuleTypeEnum.CategoryCampaign:
        return "1023,1024,1025";
      case RuleTypeEnum.IndividualProduct:
        return "492059,543628";
      case RuleTypeEnum.AllCampaignProducts:
        return "Skriv något, gäller för alla kampanjprodukter";
      case RuleTypeEnum.AllNewsProducts:
        return "Skriv något, gäller för alla skapade produkter yngre än 60 dagar";
      default:
        return "pela,ryobi";
    }
  };

  return (
    <Layout>
      <Content>
        <>
          <FullScreenLoader 
            loading={isLoadingAny} 
            message={getLoadingMessage()} 
          />
          {!isLoadingInitial && (
            <StyledCard bordered={false} className="criclebox h-full">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <Title>Produkt Ranking</Title>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <SecondaryButton
                    icon={<CalculatorOutlined />}
                    onClick={handleRecalculateRules}
                  >
                    Räkna om alla regler
                  </SecondaryButton>
                  <ModernButton icon={<PlusOutlined />} onClick={() => showModal()}>
                    Lägg till ny regel
                  </ModernButton>
                </div>
              </div>

              <ContentArea>
                <RuleList
                  rules={rules}
                  onDelete={handleDelete}
                  showModal={showModal}
                  onPageChange={handlePageChange}
                  currentPageSize={currentPageSize}
                  total={apiRulesPaginationResult?.total ?? 0}
                />
                <RankingPanel>
                  <ProductRankingList
                    pinnedItems={apiPinnedRankings?.data ?? []}
                    rankedItems={apiProductRankings?.data ?? []}
                    loading={isFetchingProductRankings || isFetchingPinnedRankings || pinMutation.isLoading || unpinMutation.isLoading || reorderPinnedMutation.isLoading}
                    rankingPage={rankingPage}
                    rankingPageSize={rankingPageSize}
                    rankingTotal={apiProductRankings?.total ?? 0}
                    searchValue={searchInput}
                    onSearchChange={setSearchInput}
                    onRankingPageChange={(page, pageSize) => {
                      setRankingPage(page);
                      setRankingPageSize(pageSize);
                    }}
                    onPin={(id) => pinMutation.mutate(id)}
                    onUnpin={(id) => unpinMutation.mutate(id)}
                    onReorderPinned={(ids) => reorderPinnedMutation.mutate(ids)}
                  />
                </RankingPanel>
              </ContentArea>
            </StyledCard>
          )}
        </>

        <Modal
          title={editingRule ? "Redigera regel" : "Lägg till ny regel"}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Namn"
              rules={[
                { required: true, message: "Ange namn!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="ruleType"
              label="Typ"
              rules={[
                { required: true, message: "Välj typ!" },
              ]}
              normalize={(value) => {
                if (value === undefined || value === null) return undefined;
                return Number.parseInt(value, 10);
              }}
            >
              <Select onChange={(value) => setSelectedRuleType(Number(value))}>
                {Object.values(RuleTypeEnum)
                  .filter(value => !isNaN(Number(value)))
                  .map(value => (
                    <Option key={value} value={value}>
                      {getRuleTypeDisplayName(Number(value))}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="expr"
              label="Regelvärde"
              rules={[
                { required: true, message: "Ange regelvärde!" },
              ]}
            >
              <Input.TextArea 
                rows={3} 
                placeholder={getExprPlaceholder(selectedRuleType)}
              />
            </Form.Item>
            <Form.Item
              name="excludeExpr"
              label="Exkludera kategorier"
              rules={[
                { required: false },
              ]}
            >
              <Input.TextArea 
                rows={3} 
                placeholder="Ange kategorier som regeln inte ska gälla för, separerade med kommatecken"
              />
            </Form.Item>
            <Form.Item
              name="effect"
              label="Effekt"
              rules={[
                { required: true, message: "Välj effekt!" },
              ]}
              normalize={(value) => {
                if (value === undefined || value === null) return undefined;
                return Number.parseInt(value, 10);
              }}
            >
              <Select>
                <Option value={RankingEffectEnum.Pin}>{getRankingEffectDisplayName(RankingEffectEnum.Pin)}</Option>
                <Option value={RankingEffectEnum.SuperBoost}>{getRankingEffectDisplayName(RankingEffectEnum.SuperBoost)}</Option>
                <Option value={RankingEffectEnum.Boost}>{getRankingEffectDisplayName(RankingEffectEnum.Boost)}</Option>
                <Option value={RankingEffectEnum.Demote}>{getRankingEffectDisplayName(RankingEffectEnum.Demote)}</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="startDate"
              label="Startdatum"
              rules={[
                { required: true, message: "Välj startdatum!" },
              ]}
            >
              <Input type="date" />
            </Form.Item>
            <Form.Item
              name="endDate"
              label="Slutdatum"
              rules={[
                { required: true, message: "Välj slutdatum!" },
              ]}
            >
              <Input type="date" />
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
}